/**
 * Ocena slowna wyniku zwyklego uzytkownika (konto `user`): "Bardzo dobrze", "Dobrze", "Srednio", "Slabo".
 *
 * Wylacznie czyste funkcje i stale, bez DOM i bez API Node: te same wartosci czyta klient (ekran wyniku) i serwer
 * (ocena koncowa treningu).
 *
 * REGULA ROBOCZA, nie metodyka. Ocena to udzial mocnych stron wsrod wskazan modelu (mocne / (mocne + do poprawy)).
 * Progi (75, 50, 25 procent) sa propozycja do uzgodnienia z autorem metodyki, a skala literowa A-D i procenty zostaja
 * na pozniej: zasada projektu to "nie pokazujemy liczby, ktorej nie ufamy". Ekran nie pokazuje udzialu jako liczby,
 * tylko slowo, a przy zbyt malej liczbie wskazan - "za malo danych" zamiast zgadywania.
 *
 * Porownania na liczbach calkowitych (mocne * 4 >= razem * 3), nie na ulamkach: 3 z 4 to dokladnie 75 procent i ma
 * wyjsc "Bardzo dobrze" bez wzgledu na to, jak zaokragla sie zmiennoprzecinkowe 0.75.
 */

export const OCENY_SLOWNE = ["slabo", "srednio", "dobrze", "bardzo-dobrze"] as const;
export type OcenaSlowna = (typeof OCENY_SLOWNE)[number];

export const ETYKIETY_OCEN_SLOWNYCH: Readonly<Record<OcenaSlowna, string>> = {
  slabo: "Słabo",
  srednio: "Średnio",
  dobrze: "Dobrze",
  "bardzo-dobrze": "Bardzo dobrze",
};

/** Najmniej wskazan w JEDNYM parametrze, zeby wolno bylo go ocenic slowem. Mniej: "za malo danych". */
export const MIN_WSKAZAN_PARAMETRU = 3;

/** Najmniej wskazan w CALEJ rozmowie (wszystkie parametry razem) dla oceny ogolnej. */
export const MIN_WSKAZAN_OGOLEM = 5;

export type WynikOceny =
  | {
      rodzaj: "ocena";
      ocena: OcenaSlowna;
      /** Polska etykieta do pokazania ("Bardzo dobrze"). */
      etykieta: string;
      mocne: number;
      doPoprawy: number;
    }
  | {
      rodzaj: "za-malo-danych";
      mocne: number;
      doPoprawy: number;
      /** Ile wskazan wymaga ta ocena (MIN_WSKAZAN_PARAMETRU albo MIN_WSKAZAN_OGOLEM). */
      potrzeba: number;
    };

/** Liczba wskazan jako nieujemna liczba calkowita: ujemne, ulamkowe i NaN nie moga zepsuc progu. */
function naLiczbeWskazan(n: number): number {
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/**
 * Ocena slowna z liczby mocnych stron i rzeczy do poprawy.
 *
 * @param minimum najmniej wskazan lacznie, zeby w ogole ocenic (domyslnie jak dla jednego parametru)
 */
export function ocenSlownie(mocne: number, doPoprawy: number, minimum: number = MIN_WSKAZAN_PARAMETRU): WynikOceny {
  const m = naLiczbeWskazan(mocne);
  const d = naLiczbeWskazan(doPoprawy);
  const razem = m + d;

  if (razem < minimum || razem === 0) {
    return { rodzaj: "za-malo-danych", mocne: m, doPoprawy: d, potrzeba: minimum };
  }

  const ocena: OcenaSlowna =
    m * 4 >= razem * 3 ? "bardzo-dobrze" : m * 2 >= razem ? "dobrze" : m * 4 >= razem ? "srednio" : "slabo";
  return { rodzaj: "ocena", ocena, etykieta: ETYKIETY_OCEN_SLOWNYCH[ocena], mocne: m, doPoprawy: d };
}

/** Ocena ogolna: wszystkie parametry razem (sumy mocnych i do poprawy), z wyzszym progiem liczby wskazan. */
export function ocenaOgolna(parametry: readonly { mocne: number; doPoprawy: number }[]): WynikOceny {
  let mocne = 0;
  let doPoprawy = 0;
  for (const p of parametry) {
    mocne += naLiczbeWskazan(p.mocne);
    doPoprawy += naLiczbeWskazan(p.doPoprawy);
  }
  return ocenSlownie(mocne, doPoprawy, MIN_WSKAZAN_OGOLEM);
}

/** Zdanie dla uzytkownika, gdy ocena slowna nie powstala. */
export function opisZaMaloDanych(wynik: Extract<WynikOceny, { rodzaj: "za-malo-danych" }>): string {
  const razem = wynik.mocne + wynik.doPoprawy;
  return razem === 0
    ? "Za mało danych: nie znaleźliśmy tu żadnej uwagi."
    : `Za mało danych: ${razem} z ${wynik.potrzeba} potrzebnych uwag, żeby to ocenić.`;
}
